import { useState } from "react";
import { Plane, ChevronDown, ChevronUp } from "lucide-react";

/* Spec §9 side-effect — Collapsible "Flights (N)" card that groups every flight
   segment on the trip page so round-trips + connections read as one journey. */

export default function FlightsRollup({ segments = [], onEdit }) {
  const flights = (segments || []).filter((s) => s.mode === "flight");
  const [open, setOpen] = useState(true);
  if (flights.length === 0) return null;

  const sorted = [...flights].sort((a, b) => {
    const da = `${a.date || ""}T${a.time || "00:00"}`;
    const db = `${b.date || ""}T${b.time || "00:00"}`;
    return da.localeCompare(db);
  });

  return (
    <div className="rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white" data-testid="flights-rollup">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-4 text-left">
        <span className="w-10 h-10 rounded-xl bg-sky-500 text-white grid place-items-center shrink-0">
          <Plane className="w-5 h-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-serif font-800 text-lg text-sky-900">Flights ({flights.length})</span>
          <span className="block text-xs text-sky-700/80 truncate">
            {sorted[0].from_place || "?"} → {sorted[sorted.length - 1].to_place || "?"}
            {flights.length > 1 && ` · ${flights.length - 1} connection${flights.length === 2 ? "" : "s"}`}
          </span>
        </span>
        {open ? <ChevronUp className="w-5 h-5 text-sky-700" /> : <ChevronDown className="w-5 h-5 text-sky-700" />}
      </button>
      {open && (
        <ul className="border-t border-sky-100 divide-y divide-sky-100" data-testid="flights-rollup-list">
          {sorted.map((f, i) => (
            <li key={f.id || i} className="flex items-center gap-3 p-3 hover:bg-sky-50 transition"
              data-testid={`flight-leg-${f.id || i}`}>
              <div className="w-8 h-8 rounded-full bg-white border border-sky-200 grid place-items-center text-[11px] font-800 text-sky-700 shrink-0">
                {i + 1}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-700 text-slate-800 truncate">
                  {f.carrier || "Airline"} {f.ref || ""} · {f.from_place || "?"} → {f.to_place || "?"}
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {f.date || "—"} {f.time || ""} {f.seat && `· Seat ${f.seat}`}
                </div>
              </div>
              {onEdit && (
                <button onClick={() => onEdit(f)} className="text-xs text-blue-600 font-700 hover:underline"
                  data-testid={`flight-leg-edit-${f.id || i}`}>
                  Edit
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
